import { FC, useState } from 'react';
import { Popover, Tooltip } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { updateActivityStatus } from 'store/container/auth/auth-slice';
import apiRequests from 'utils/api';
import apiRoutes from 'config/apiRoute';
import { ActivityStatus } from 'types';
import useWebSocket from 'utils/useWebSocket';

import styles from './index.module.less';

const STATUS_CONFIG: Record<ActivityStatus, { color: string; label: string }> = {
  online: { color: '#48E330', label: 'Online' },
  away: { color: '#FAAD14', label: 'Away' },
  offline: { color: '#9A9A9A', label: 'Offline' },
};

const STATUS_ORDER: ActivityStatus[] = ['online', 'away', 'offline'];

interface ActivityStatusIndicatorProps {
  className?: string;
}

const ActivityStatusIndicator: FC<ActivityStatusIndicatorProps> = ({ className }) => {
  const currentStatus = useSelector((store: any) => store.auth.activityStatus) as ActivityStatus;
  const currentUser = useSelector((store: any) => store.auth.user);
  const dispatch = useDispatch();
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [popoverVisible, setPopoverVisible] = useState(false);
  const [loading, setLoading] = useState(false);

  // Listen for WebSocket updates to activity status
  useWebSocket({
    channelName: 'model.changes',
    privateChannel: true,
    listen: {
      event: '.app.models.auth.user',
      callback: (event: any) => {
        try {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          const { activity_status } = event.payload || event;
          const { id } = event;

          // Only update if it's for the current user
          if (currentUser && id === currentUser.uuid && activity_status !== undefined) {
            dispatch(updateActivityStatus(activity_status));
          }
        } catch (error) {
          console.error('Error processing activity status event:', error);
        }
      },
    },
  });

  const handleStatusChange = async (status: ActivityStatus) => {
    if (status === currentStatus || loading) return;

    const previousStatus = currentStatus;
    setLoading(true);
    setPopoverVisible(false);
    setTooltipVisible(false);

    dispatch(updateActivityStatus(status));

    try {
      await apiRequests.put(apiRoutes.USER_ACTIVITY_STATUS, { activity_status: status });
    } catch (error) {
      dispatch(updateActivityStatus(previousStatus));
    } finally {
      setLoading(false);
    }
  };

  const statusOptions = (
    <div className={styles.statusPicker}>
      {STATUS_ORDER.map((status) => (
        <div
          key={status}
          className={`${styles.statusOption} ${status === currentStatus ? styles.active : ''}`}
          onClick={() => handleStatusChange(status)}
        >
          <span className={styles.dot} style={{ backgroundColor: STATUS_CONFIG[status].color }} />
          <span>{STATUS_CONFIG[status].label}</span>
        </div>
      ))}
    </div>
  );

  return (
    <span onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
      <Popover
        content={statusOptions}
        trigger="click"
        placement="bottomRight"
        visible={popoverVisible}
        onVisibleChange={setPopoverVisible}
        overlayClassName={styles.statusPopoverOverlay}
        destroyTooltipOnHide
      >
        <span
          className={`${styles.statusDot} ${className ?? ''}`}
          style={{ backgroundColor: STATUS_CONFIG[currentStatus].color }}
        />
      </Popover>
    </span>
  );
};

export default ActivityStatusIndicator;
